import { useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select, { type SelectOption } from "../../../components/ui/Select";
import { useToast } from "../../../contexts/ToastContext";
import { useUploadImageMutation } from "../../../store/api/imageUploadApi";
import { useListPropertiesQuery } from "../../../store/api/propertiesApi";
import {
	useCreateUserMutation,
	useUpdateUserMutation,
} from "../../../store/api/usersApi";
import type { User, UserRole } from "../../../types";

interface CreateUserFormProps {
	onSuccess: () => void;
	onCancel: () => void;
	user?: User | null; // User to edit (if provided, form is in edit mode)
}

const ROLE_OPTIONS: SelectOption[] = [
	{ value: "admin", label: "Administrator" },
	{ value: "tenant", label: "Tenant" },
	{ value: "technician", label: "Technician" },
	{ value: "agent", label: "Sales Agent" },
];

const CreateUserForm: React.FC<CreateUserFormProps> = ({
	onSuccess,
	onCancel,
	user,
}) => {
	const { error: showError, success: showSuccess } = useToast();
	const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
	const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
	const [uploadImage, { isLoading: isUploadingImage }] =
		useUploadImageMutation();
	const isEditMode = !!user;
	const isLoading = isCreating || isUpdating;
	// Pre-select tenant role if used from tenant management
	const [selectedRole, setSelectedRole] = useState<UserRole | "">(
		window.location.pathname.includes("/tenant-management") ? "tenant" : "",
	);

	// Fetch properties for tenant assignment
	const { data: propertiesData } = useListPropertiesQuery(
		{ page_size: 100 },
		{ skip: selectedRole !== "tenant" },
	);

	// Helper function to calculate rent type based on lease duration
	const calculateRentType = (startDate: string, endDate: string): string => {
		if (!startDate || !endDate) return "";

		const start = new Date(startDate);
		const end = new Date(endDate);
		const diffTime = Math.abs(end.getTime() - start.getTime());
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		const diffMonths = Math.round(diffDays / 30);

		// Determine rent type based on duration
		if (diffDays <= 31) return "monthly"; // ~1 month
		else if (diffMonths >= 5 && diffMonths <= 7) return "biannual"; // ~6 months
		else if (diffMonths >= 11 && diffMonths <= 13)
			return "annual"; // ~12 months
		else if (diffMonths >= 23 && diffMonths <= 25)
			return "biannual"; // ~24 months (2 years)
		else return `${diffMonths} months`; // Custom duration
	};

	const [formData, setFormData] = useState({
		// Basic fields
		email: "",
		first_name: "",
		last_name: "",
		phone: "",
		// Tenant fields
		property_id: "",
		lease_code: "",
		property_address: "",
		unit_number: "",
		emergency_contact_name: "",
		emergency_contact_phone: "",
		lease_start_date: "",
		lease_end_date: "",
		monthly_rent: "",
		// Technician fields
		primary_specialty: "",
		experience: "",
		license_number: "",
		service_area: "",
		additional_specialties: [] as string[],
		// Agent fields
		brokerage_name: "",
		service_areas: "",
		property_types: [] as string[],
		// Admin fields
		company_name: "",
		job_title: "",
		company_address: "",
		property_count: "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [successMessage, setSuccessMessage] = useState("");

	// Populate form when user is provided (edit mode)
	useEffect(() => {
		if (user && isEditMode) {
			setFormData({
				email: user.email || "",
				first_name: user.first_name || "",
				last_name: user.last_name || "",
				phone: user.phone || "",
				property_id: "", // Will be set from lease if available
				lease_code: user.lease_code || "",
				property_address: user.property_address || "",
				unit_number: user.unit_number || "",
				emergency_contact_name: user.emergency_contact_name || "",
				emergency_contact_phone: user.emergency_contact_phone || "",
				lease_start_date: "",
				lease_end_date: "",
				monthly_rent: "",
				primary_specialty: user.primary_specialty || "",
				experience: user.experience || "",
				license_number: user.license_number || "",
				service_area: (user as any).service_area || "",
				additional_specialties: (user as any).additional_specialties || [],
				brokerage_name: user.brokerage_name || "",
				service_areas: user.service_areas || "",
				property_types: (user as any).property_types || [],
				company_name: user.company_name || "",
				job_title: user.job_title || "",
				company_address: (user as any).company_address || "",
				property_count: (user as any).property_count || "",
			});
			setSelectedRole(user.role || "");
		}
	}, [user, isEditMode]);

	// Lease document upload state
	const [leaseDocument, setLeaseDocument] = useState<File | null>(null);
	const [leaseDocumentPreview, setLeaseDocumentPreview] = useState<
		string | null
	>(null);
	const [isUploadingDocument, setIsUploadingDocument] = useState(false);

	const handleInputChange = (field: string, value: string | string[]) => {
		// Normalize email to lowercase
		const normalizedValue =
			field === "email" && typeof value === "string"
				? value.toLowerCase()
				: value;
		setFormData((prev) => ({ ...prev, [field]: normalizedValue }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	// Handle lease document file selection
	const handleLeaseDocumentChange = (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = e.target.files?.[0];
		if (!file) {
			setErrors({ ...errors, leaseDocument: "Please select a file" });
			return;
		}

		// Validate file type - PDF and images only (JPG, JPEG, PNG - no GIF or WebP)
		const allowedTypes = [
			"application/pdf",
			"image/jpeg",
			"image/jpg",
			"image/png",
		];
		const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png"];

		const isValidType = allowedTypes.includes(file.type?.toLowerCase());
		const isValidExtension = allowedExtensions.some((ext) =>
			file.name?.toLowerCase().endsWith(ext),
		);

		if (!isValidType && !isValidExtension) {
			setErrors({
				...errors,
				leaseDocument:
					"Please select a PDF or image file (JPG, JPEG, PNG only)",
			});
			return;
		}

		// Validate file size (10MB max for documents)
		const maxSize = 10 * 1024 * 1024; // 10MB in bytes
		if (file.size > maxSize) {
			setErrors({
				...errors,
				leaseDocument: "File size must be less than 10MB",
			});
			return;
		}

		// Validate minimum file size
		if (file.size < 1024) {
			setErrors({
				...errors,
				leaseDocument:
					"File size is too small. Please select a valid document.",
			});
			return;
		}

		// Clear previous errors
		setErrors((prev) => ({ ...prev, leaseDocument: "" }));

		// Set file and create preview for images
		setLeaseDocument(file);

		// Only create preview for images
		if (file.type.startsWith("image/")) {
			const reader = new FileReader();
			reader.onloadend = () => {
				if (reader.result && typeof reader.result === "string") {
					setLeaseDocumentPreview(reader.result);
				}
			};
			reader.onerror = () => {
				setErrors({
					...errors,
					leaseDocument: "Failed to read file. Please try again.",
				});
				setLeaseDocument(null);
				setLeaseDocumentPreview(null);
			};
			reader.readAsDataURL(file);
		} else {
			// For PDFs, just show filename
			setLeaseDocumentPreview(null);
		}
	};

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.email.trim()) {
			newErrors.email = "Email is required";
		} else if (!/\S+@\S+\.\S+/.test(formData.email)) {
			newErrors.email = "Please enter a valid email address";
		}

		if (!formData.first_name.trim()) {
			newErrors.first_name = "First name is required";
		}

		if (!formData.last_name.trim()) {
			newErrors.last_name = "Last name is required";
		}

		if (!formData.phone.trim()) {
			newErrors.phone = "Phone number is required";
		}

		if (!selectedRole) {
			newErrors.role = "Please select a role";
		}

		// Role-specific validations
		if (selectedRole === "tenant") {
			// Tenant fields are optional but can be validated if needed
		} else if (selectedRole === "technician") {
			// Technician fields are optional
		} else if (selectedRole === "agent") {
			// Agent fields are optional
		} else if (selectedRole === "admin") {
			// Admin fields are optional
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validate()) {
			return;
		}

		setErrors({});
		setSuccessMessage("");

		try {
			// Upload lease document to Cloudinary if provided
			let leaseDocumentUrl = "";
			if (leaseDocument && selectedRole === "tenant") {
				setIsUploadingDocument(true);
				try {
					// Convert file to base64
					const base64 = await new Promise<string>((resolve, reject) => {
						const reader = new FileReader();
						reader.onloadend = () => {
							if (!reader.result || typeof reader.result !== "string") {
								reject(new Error("Failed to read file"));
								return;
							}
							// Remove data:...;base64, prefix
							const parts = reader.result.split(",");
							if (parts.length < 2) {
								reject(new Error("Invalid file format"));
								return;
							}
							resolve(parts[1]);
						};
						reader.onerror = () => reject(new Error("Failed to read file"));
						reader.readAsDataURL(leaseDocument);
					});

					// Upload to Cloudinary
					const uploadResult = await uploadImage({
						images: [base64],
						folder: "lease_documents",
					}).unwrap();

					if (!uploadResult.uploaded || uploadResult.uploaded.length === 0) {
						const errorMsg =
							uploadResult.failed?.[0]?.error ||
							"Failed to upload lease document. Please try again.";
						throw new Error(errorMsg);
					}

					const uploadedDoc = uploadResult.uploaded[0];
					if (!uploadedDoc?.url) {
						throw new Error(
							"Document upload succeeded but no URL was returned.",
						);
					}

					leaseDocumentUrl = uploadedDoc.url;
				} catch (uploadError: any) {
					console.error("Lease document upload error:", uploadError);
					setIsUploadingDocument(false);
					showError(uploadError.message || "Failed to upload lease document");
					return;
				} finally {
					setIsUploadingDocument(false);
				}
			}

			// Normalize email to lowercase before sending
			const normalizedEmail = formData.email.trim().toLowerCase();
			// Prepare data based on role
			const userData: any = {
				email: normalizedEmail,
				first_name: formData.first_name,
				last_name: formData.last_name,
				phone: formData.phone,
				role: selectedRole,
			};

			// Add role-specific fields
			if (selectedRole === "tenant") {
				if (formData.property_id) userData.property_id = formData.property_id;
				// Note: lease_code is now auto-generated on the Lease model, not User model
				// Keeping for backward compatibility but it won't be used
				if (formData.property_address)
					userData.property_address = formData.property_address;
				if (formData.unit_number) userData.unit_number = formData.unit_number;
				if (formData.emergency_contact_name)
					userData.emergency_contact_name = formData.emergency_contact_name;
				if (formData.emergency_contact_phone)
					userData.emergency_contact_phone = formData.emergency_contact_phone;
				// Lease creation fields
				if (formData.lease_start_date) {
					// Convert date to ISO string
					const startDate = new Date(formData.lease_start_date);
					userData.lease_start_date = startDate.toISOString();
				}
				if (formData.lease_end_date) {
					// Convert date to ISO string
					const endDate = new Date(formData.lease_end_date);
					userData.lease_end_date = endDate.toISOString();
				}
				if (formData.monthly_rent)
					userData.monthly_rent = parseFloat(formData.monthly_rent);
				// Add lease document URL if uploaded
				if (leaseDocumentUrl) {
					userData.lease_document_url = leaseDocumentUrl;
				}
			} else if (selectedRole === "technician") {
				if (formData.primary_specialty)
					userData.primary_specialty = formData.primary_specialty;
				if (formData.experience) userData.experience = formData.experience;
				if (formData.license_number)
					userData.license_number = formData.license_number;
				if (formData.service_area)
					userData.service_area = formData.service_area;
				if (formData.additional_specialties.length > 0)
					userData.additional_specialties = formData.additional_specialties;
			} else if (selectedRole === "agent") {
				if (formData.brokerage_name)
					userData.brokerage_name = formData.brokerage_name;
				if (formData.service_areas)
					userData.service_areas = formData.service_areas;
				if (formData.property_types.length > 0)
					userData.property_types = formData.property_types;
				if (formData.license_number)
					userData.license_number = formData.license_number;
				if (formData.experience) userData.experience = formData.experience;
			} else if (selectedRole === "admin") {
				if (formData.company_name)
					userData.company_name = formData.company_name;
				if (formData.job_title) userData.job_title = formData.job_title;
				if (formData.company_address)
					userData.company_address = formData.company_address;
				if (formData.property_count)
					userData.property_count = formData.property_count;
			}

			if (isEditMode && user) {
				// Update existing user
				await updateUser({ id: user.id, data: userData }).unwrap();
				showSuccess("Tenant updated successfully");
				setTimeout(() => {
					onSuccess();
				}, 1000);
			} else {
				// Create new user
				const result = await createUser(userData).unwrap();
				setSuccessMessage(result.message);
				setTimeout(() => {
					onSuccess();
				}, 2000);
			}
		} catch (error: any) {
			console.error("User creation error:", error);

			if (error?.data?.error) {
				setErrors({ submit: error.data.error });
			} else if (error?.data?.email) {
				setErrors({
					email: Array.isArray(error.data.email)
						? error.data.email[0]
						: error.data.email,
				});
			} else {
				setErrors({
					submit: isEditMode
						? "Failed to update user. Please try again."
						: "Failed to create user. Please try again.",
				});
			}
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<div className="flex items-center justify-between mb-6">
				<h2 className="text-2xl font-semibold text-foreground">
					{isEditMode ? "Edit Tenant" : "Create New User"}
				</h2>
			</div>

			{successMessage && (
				<div className="rounded-lg bg-success/20 border border-success/30 p-4 text-success">
					<div className="flex items-center space-x-2">
						<Icon name="CheckCircle" size={20} />
						<p className="font-medium">{successMessage}</p>
					</div>
				</div>
			)}

			{/* Basic Information */}
			<div className="space-y-4">
				<h3 className="text-lg font-medium text-foreground">
					Basic Information
				</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<Input
						label="Email Address"
						type="email"
						value={formData.email}
						onChange={(e) => handleInputChange("email", e.target.value)}
						error={errors.email}
						required
						disabled={isEditMode}
					/>

					<Input
						label="Phone Number"
						type="tel"
						value={formData.phone}
						onChange={(e) => handleInputChange("phone", e.target.value)}
						error={errors.phone}
						required
					/>

					<Input
						label="First Name"
						type="text"
						value={formData.first_name}
						onChange={(e) => handleInputChange("first_name", e.target.value)}
						error={errors.first_name}
						required
					/>

					<Input
						label="Last Name"
						type="text"
						value={formData.last_name}
						onChange={(e) => handleInputChange("last_name", e.target.value)}
						error={errors.last_name}
						required
					/>

					<div className="md:col-span-2">
						<label className="mb-2 block text-sm font-medium text-foreground">
							Role <span className="text-destructive">*</span>
						</label>
						<Select
							value={selectedRole}
							options={ROLE_OPTIONS}
							onValueChange={(value) => {
								setSelectedRole(value as UserRole);
								setErrors((prev) => ({ ...prev, role: "" }));
								// Reset role-specific fields when role changes
								if (value !== "tenant") {
									setFormData((prev) => ({
										...prev,
										property_id: "",
										lease_start_date: "",
										lease_end_date: "",
										monthly_rent: "",
									}));
								}
							}}
							required
							disabled={isEditMode}
						/>
						{errors.role && (
							<p className="text-sm text-destructive mt-1">{errors.role}</p>
						)}
					</div>
				</div>
			</div>

			{/* Role-Specific Fields */}
			{selectedRole && (
				<div className="space-y-4 pt-6 border-t border-border">
					<h3 className="text-lg font-medium text-foreground">
						{selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}{" "}
						Details
					</h3>

					{selectedRole === "tenant" && (
						<div className="space-y-4">
							{/* Property Assignment */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="md:col-span-2">
									<label className="mb-2 block text-sm font-medium text-foreground">
										Assign to Property{" "}
										<span className="text-muted-foreground text-xs">
											(Optional)
										</span>
									</label>
									<Select
										value={formData.property_id}
										options={[
											{ value: "", label: "No property assigned" },
											...(propertiesData?.results
												?.filter(
													(p) =>
														p.status === "available" ||
														p.status === "leased" ||
														p.status === "sold",
												)
												.map((p) => ({
													value: p.id,
													label: `${p.name} - ${p.address}${
														p.rent_amount || p.monthly_rent
															? ` (₦${(
																	p.rent_amount || p.monthly_rent
															  )?.toLocaleString()})`
															: ""
													}`,
												})) || []),
										]}
										onValueChange={(value) => {
											const propertyId = String(value || "");
											handleInputChange("property_id", propertyId);
											// Auto-populate rent amount and rent type if property is selected
											if (propertyId) {
												const selectedProperty = propertiesData?.results?.find(
													(p) => p.id === propertyId,
												);
												if (selectedProperty) {
													const rentAmount =
														selectedProperty.rent_amount ||
														selectedProperty.monthly_rent;
													if (rentAmount) {
														handleInputChange(
															"monthly_rent",
															rentAmount.toString(),
														);
													}
													if (selectedProperty.address) {
														handleInputChange(
															"property_address",
															selectedProperty.address,
														);
													}
												}
											} else {
												// Clear fields if no property selected
												handleInputChange("monthly_rent", "");
												handleInputChange("property_address", "");
											}
										}}
									/>
									<p className="text-xs text-muted-foreground mt-1">
										Select a property to assign this tenant to. This will create
										a lease if dates are provided.
									</p>
								</div>
							</div>

							{/* Lease Information (shown when property is selected) */}
							{formData.property_id && (
								<div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-4">
									<h4 className="text-sm font-semibold text-foreground">
										Lease Information
									</h4>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<Input
											label="Lease Start Date"
											type="date"
											value={formData.lease_start_date}
											onChange={(e) =>
												handleInputChange("lease_start_date", e.target.value)
											}
											description="Required to create lease"
										/>
										<Input
											label="Lease End Date"
											type="date"
											value={formData.lease_end_date}
											onChange={(e) =>
												handleInputChange("lease_end_date", e.target.value)
											}
											description="Required to create lease"
										/>
										<Input
											label={`Rent Amount${
												formData.lease_start_date && formData.lease_end_date
													? ` (${calculateRentType(
															formData.lease_start_date,
															formData.lease_end_date,
													  )})`
													: ""
											}`}
											type="number"
											value={formData.monthly_rent}
											onChange={(e) =>
												handleInputChange("monthly_rent", e.target.value)
											}
											description="Auto-filled from selected property"
										/>
									</div>
									{formData.lease_start_date && formData.lease_end_date && (
										<div className="flex items-center gap-2 text-sm text-primary">
											<Icon name="Info" size={16} />
											<span>
												Lease Type:{" "}
												<strong className="capitalize">
													{calculateRentType(
														formData.lease_start_date,
														formData.lease_end_date,
													)}
												</strong>{" "}
												(
												{Math.ceil(
													Math.abs(
														new Date(formData.lease_end_date).getTime() -
															new Date(formData.lease_start_date).getTime(),
													) /
														(1000 * 60 * 60 * 24),
												)}{" "}
												days)
											</span>
										</div>
									)}
									<p className="text-xs text-muted-foreground">
										If lease dates are provided, a lease will be automatically
										created and the property status will be set to "Leased".
									</p>
								</div>
							)}

							{/* Other Tenant Fields */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{/* Lease Code is now auto-generated when lease is created */}
								{/* Removed manual lease_code input - it will be auto-generated */}
								<Input
									label="Unit Number"
									type="text"
									value={formData.unit_number}
									onChange={(e) =>
										handleInputChange("unit_number", e.target.value)
									}
									description="Optional"
								/>
								<Input
									label="Property Address"
									type="text"
									value={formData.property_address}
									onChange={(e) =>
										handleInputChange("property_address", e.target.value)
									}
									description="Auto-filled if property selected"
									className="md:col-span-2"
								/>
								<Input
									label="Emergency Contact Name"
									type="text"
									value={formData.emergency_contact_name}
									onChange={(e) =>
										handleInputChange("emergency_contact_name", e.target.value)
									}
									description="Optional"
								/>
								<Input
									label="Emergency Contact Phone"
									type="tel"
									value={formData.emergency_contact_phone}
									onChange={(e) =>
										handleInputChange("emergency_contact_phone", e.target.value)
									}
									description="Optional"
								/>
							</div>
						</div>
					)}

					{selectedRole === "technician" && (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<Input
								label="Primary Specialty"
								type="text"
								value={formData.primary_specialty}
								onChange={(e) =>
									handleInputChange("primary_specialty", e.target.value)
								}
								description="Optional"
							/>
							<Input
								label="Experience"
								type="text"
								value={formData.experience}
								onChange={(e) =>
									handleInputChange("experience", e.target.value)
								}
								description="Optional (e.g., '5 years')"
							/>
							<Input
								label="License Number"
								type="text"
								value={formData.license_number}
								onChange={(e) =>
									handleInputChange("license_number", e.target.value)
								}
								description="Optional"
							/>
							<Input
								label="Service Area"
								type="text"
								value={formData.service_area}
								onChange={(e) =>
									handleInputChange("service_area", e.target.value)
								}
								description="Optional"
							/>
						</div>
					)}

					{selectedRole === "agent" && (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<Input
								label="Brokerage Name"
								type="text"
								value={formData.brokerage_name}
								onChange={(e) =>
									handleInputChange("brokerage_name", e.target.value)
								}
								description="Optional"
							/>
							<Input
								label="License Number"
								type="text"
								value={formData.license_number}
								onChange={(e) =>
									handleInputChange("license_number", e.target.value)
								}
								description="Optional"
							/>
							<Input
								label="Experience"
								type="text"
								value={formData.experience}
								onChange={(e) =>
									handleInputChange("experience", e.target.value)
								}
								description="Optional (e.g., '3 years')"
							/>
							<Input
								label="Service Areas"
								type="text"
								value={formData.service_areas}
								onChange={(e) =>
									handleInputChange("service_areas", e.target.value)
								}
								description="Optional"
								className="md:col-span-2"
							/>
						</div>
					)}

					{selectedRole === "admin" && (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<Input
								label="Company Name"
								type="text"
								value={formData.company_name}
								onChange={(e) =>
									handleInputChange("company_name", e.target.value)
								}
								description="Optional"
							/>
							<Input
								label="Job Title"
								type="text"
								value={formData.job_title}
								onChange={(e) => handleInputChange("job_title", e.target.value)}
								description="Optional"
							/>
							<Input
								label="Company Address"
								type="text"
								value={formData.company_address}
								onChange={(e) =>
									handleInputChange("company_address", e.target.value)
								}
								description="Optional"
								className="md:col-span-2"
							/>
							<Input
								label="Property Count"
								type="text"
								value={formData.property_count}
								onChange={(e) =>
									handleInputChange("property_count", e.target.value)
								}
								description="Optional"
							/>
						</div>
					)}
				</div>
			)}

			{errors.submit && (
				<div className="rounded-lg bg-destructive/20 border border-destructive/30 p-3 text-sm text-destructive">
					{errors.submit}
				</div>
			)}

			<div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
				<Button
					type="button"
					variant="outline"
					onClick={onCancel}
					disabled={isLoading}
				>
					Cancel
				</Button>
				<Button type="submit" disabled={isLoading}>
					{isLoading ? (
						<>
							<Icon name="Loader2" size={16} className="mr-2 animate-spin" />
							Creating User...
						</>
					) : (
						<>
							<Icon name="UserPlus" size={16} className="mr-2" />
							Create User
						</>
					)}
				</Button>
			</div>
		</form>
	);
};

export default CreateUserForm;
